// TerrainDebug — the `window.lfTerrainDebug` deep-inspection hook (Set12 debugging).
// Kept OUT of TerrainRenderer.tsx so the renderer stays uncluttered; the renderer pushes
// its LIVE refs here each render body via {@link terrainDebugUpdate}, and an external tool
// (e.g. the agent browser session) reads a snapshot via `window.lfTerrainDebug.refresh()`.
import type {MaterialRegion} from "../../Terrain/Materials/Splat/MaterialRegions.js";
import type {TerrainQuadtree, QuadtreeNode} from "./TerrainQuadtree.js";
import type {TerrainMaterialManager} from "../../Terrain/Materials/TerrainMaterialManager.js";

/** The live terrain-renderer state the hook snapshots. Re-pushed every render body. */
export interface TerrainDebugState {
	regions: MaterialRegion[];
	qt: TerrainQuadtree|null;
	manager: TerrainMaterialManager|null;
	splatMap: Map<QuadtreeNode, {indexTex: any; weightTex: any; regionTex?: any; paramsTex?: any; slotDominance?: number[]; texVersion: number}>|null;
	chunkMaterialMap: Map<QuadtreeNode, any>|null;
	baseRecipeID: string;
	splatEnabled: boolean;
	materialProgSetHash: string;
	/** The raw WebGL2 context (devtools deep-inspection + the program-compile counter). */
	gl?: WebGL2RenderingContext|null;
	/** The three.js WebGLRenderer (devtools deep-inspection of renderer.info.programs). */
	renderer?: unknown|null;
	/** How many terrain mesh draws were submitted last frame (vs renderer.info.render.calls total). */
	activeMeshCount?: number|null;
	/** Set7 — how many chunks fell back to variant row 0 (all-zeros → black) this frame. */
	variantRow0FallbackCount?: number|null;
}

let state: TerrainDebugState = {
	regions: [],
	qt: null,
	manager: null,
	splatMap: null,
	chunkMaterialMap: null,
	baseRecipeID: "dirt",
	splatEnabled: false,
	materialProgSetHash: "",
	gl: null,
	renderer: null,
	activeMeshCount: null,
	variantRow0FallbackCount: null,
};

/** Re-point the snapshot source to the LIVE renderer state (call each render body). */
export function terrainDebugUpdate(s: TerrainDebugState): void {
	state = s;
	// Install the program-compile counter once per GL context (a WebGLProgram's first use calls
	// `gl.getProgramInfoLog` — three's `checkShaderErrors` is ON by default (WebGLRenderer.js:119),
	// and that synchronous readback is the hot path in the perf profile).  Counting invocations +
	// sampling a light stack tells us HOW MANY programs are being created + when (cache miss /
	// per-chunk distinct set / every-frame leak).  Non-invasive: wraps once, delegates to the real fn.
	if (s.gl && (s.gl as any).__progDiagInstalled !== true) {
		const gl = s.gl;
		const real = gl.getProgramInfoLog.bind(gl);
		const counter: any = {total: 0, sinceCheck: 0, byCaller: {}};
		const t0 = performance.now();
		(gl as any).__progDiagInstalled = true;
		(gl as any).getProgramInfoLog = (prog: WebGLProgram) => {
			counter.total++;
			counter.sinceCheck++;
			// Aggregate by the caller that created the program (a deeper stack — the shallow 4
			// frames all look identical because getProgramInfoLog is called synchronously inside
			// onFirstUse).  This distinguishes warm-up / chunk-clone / fast-path / other systems.
			try {
				const st = new Error().stack ?? "";
				const lines = st.split("\n");
				// Skip the first 3 (Error / getProgramInfoLog / onFirstUse) and capture ~6 frames.
				const key = lines.slice(3, 9).map(l=>l.trim().replace(/^at /, "").split(":")[0]).join(" <- ");
				counter.byCaller[key] = (counter.byCaller[key] ?? 0) + 1;
			} catch { /* ignore */ }
			// Record a coarse time-bucket so we can see if creates cluster in bursts (warm-up /
			// a single zoom frame) vs spread evenly (per-frame leak).
			const ms = Math.floor((performance.now() - t0) / 500) * 500;
			counter.buckets = counter.buckets ?? {};
			counter.buckets[`${ms}ms`] = (counter.buckets[`${ms}ms`] ?? 0) + 1;
			return real(prog);
		};
		(gl as any).__progCounter = counter;

		// 2026-08-25 — also count `uniformMatrix4fv` calls (the "setValue → setValueM4" hot path
		// in the second profile).  This runs once per matrix per draw (three skips it only when
		// the same program + same matrix value is re-drawn consecutively — the terrain's
		// per-chunk transforms defeat that).  A high per-frame count = too many draws / matrices.
		const realM4 = gl.uniformMatrix4fv.bind(gl);
		const m4counter: any = {total: 0, lastFrameDelta: 0, frameTotal: 0, frameStart: performance.now()};
		(gl as any).uniformMatrix4fv = (loc: WebGLUniformLocation, transpose: boolean, v: Float32Array) => {
			m4counter.total++;
			// Rough per-frame estimate: reset a frame bucket whenever ~16ms has passed since the
			// last reset (a proxy for "per frame" without hooking the RAF loop).
			const now = performance.now();
			if (now - m4counter.frameStart >= 16) {
				m4counter.lastFrameDelta = m4counter.total - (m4counter.frameTotal ?? 0);
				m4counter.frameTotal = m4counter.total;
				m4counter.frameStart = now;
			}
			return realM4(loc, transpose, v);
		};
		(gl as any).__m4Counter = m4counter;
	}
}

/** Program-compile diagnostics — call `window.lfTerrainDebug.programDiag()` for a fresh read. */
function terrainProgramDiag(): any {
	const gl = state.gl as any;
	const counter = gl?.__progCounter ?? {total: 0, sinceCheck: 0};
	const renderer = state.renderer as any;
	const out: any = {
		// How many WebGLPrograms three.js has compiled in total (getProgramInfoLog calls).
		programInfoLogCalls: counter.total,
		sinceLastCall: counter.sinceCheck,
		firstStack: counter.firstStack,
		lastStack: counter.lastStack,
		// Program creations aggregated by CALLER (deeper stack) — pinpoints the churn source.
		byCaller: counter.byCaller ?? {},
		// Program creations bucketed by wall-time (500ms) — bursts = warm-up / one zoom frame;
		// uniform spread = per-frame leak.
		buckets: counter.buckets ?? {},
		// three's live program registry + its current size.
		rendererProgramCount: renderer?.info?.programs?.length ?? null,
		rendererPrograms: renderer?.info?.programs ?? null,	// Matrix uploads (the second-profile hot path) — high per-frame = too many draws/matrices.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	m4Counter: (gl?.__m4Counter as any) ?? null,
	// three's per-frame render stats (draw calls, triangles).
	renderCalls: renderer?.info?.render?.calls ?? null,
	renderTriangles: renderer?.info?.render?.triangles ?? null,
	// How many of those draws are the terrain (chunk meshes) vs the scene total.
	terrainActiveMeshes: state.activeMeshCount ?? null,
	// Set7 black-terrain probe: how many chunks fell back to variant row 0 (all-zeros → black).
	variantRow0Fallback: state.variantRow0FallbackCount ?? null,		// The manager's per-set program cache (setHash -> {recipeIDs, material}).
		managerPrograms: state.manager ? [...(state.manager as any).programs.entries()].map(([hash, p]: [string, any])=>({
			setHash: hash,
			recipeIDs: p?.recipeIDs,
			fragmentHash: p?.material ? (p.material.fragmentShader ?? "").length : null,
		})) : null,
		managerVariants: state.manager ? (state.manager as any).variants?.size ?? null : null,
	};
	// Directly answer "is the fragment shader per-chunk distinct?" — count DISTINCT fragment
	// strings + distinct program objects across the live chunk materials.  If distinctFragments
	// is ~ the chunk count, the cache key is exploding per chunk (the >10s in getProgramInfoLog).
	const chunkMat = state.chunkMaterialMap;
	if (chunkMat) {
		const fragSet = new Set<string>();
		const progSet = new Set<number>();
		let sample: string | null = null;
		for (const m of chunkMat.values()) {
			const fs = (m as any)?.fragmentShader ?? "";
			fragSet.add(fs);
			// The three program object id (stable across materials that share it).
			const pid = (m as any)?.program?.id ?? (m as any)?.program?.uuid ?? null;
			if (pid != null) progSet.add(pid);
			if (!sample) sample = fs.slice(0, 200);
		}
		out.liveChunks = chunkMat.size;
		out.distinctFragmentStrings = fragSet.size;
		out.distinctProgramObjects = progSet.size;
		out.sampleFragmentHead = sample;
		// Set7 black-terrain probe — dump a FEW chunk materials' key uniforms to see WHY the
		// terrain renders black: the base albedo (u_baseAlbedo), the variant row, and whether the
		// splat/variant textures are bound.  (Black base albedo ⇒ hypsometric-ramp issue; black
		// variant row ⇒ params degenerate; missing splat bind ⇒ the splat path isn't running.)
		out.sampleChunkUniforms = [...chunkMat.values()].slice(0, 3).map((m: any)=>({
			u_baseAlbedo: m?.uniforms?.u_baseAlbedo?.value?.toArray?.() ?? m?.uniforms?.u_baseAlbedo?.value ?? null,
			u_variantRow: m?.uniforms?.u_variantRow?.value ?? null,
			u_variantParams_bound: !!m?.uniforms?.u_variantParams?.value,
			u_splatIndex_bound: !!m?.uniforms?.u_splatIndex?.value,
			u_splatDist_bound: !!m?.uniforms?.u_splatDist?.value,
			u_splatDist2_bound: !!m?.uniforms?.u_splatDist2?.value,
			u_chunkSize: m?.uniforms?.u_chunkSize?.value ?? null,
		}));
	}
	// ── Set7 UBO/binding-point pressure (2026-08-25): distinct live UniformsGroups (≈ binding
	//    points) vs the GL's MAX_UNIFORM_BUFFER_BINDINGS.  Scratch+template share one group per
	//    program, so this should be ≈ program count, not 2×.
	const manager = state.manager as any;
	const liveUboIds = new Set<number>();
	const liveUboBySource: Record<string, number> = {};
	const countGroups = (label: string, mats: Iterable<any>): void => {
		let n = 0;
		for (const m of mats) {
			for (const g of m?.uniformsGroups ?? []) {
				liveUboIds.add(g.id);
				n++;
			}
		}
		if (n > 0) liveUboBySource[label] = n;
	};
	if (manager) {
		// The manager's live per-set program templates.
		countGroups("programTemplates", [...(manager.programs?.values() ?? [])].map((p: any)=>p.material));
		// The warm keep-alive scratch materials (each shares its program's group — no extra UBOs).
		countGroups("warmScratches", [...(manager.warmMaterials?.values() ?? [])].map((w: any)=>w.material));
	}
	// The chunk materials actually rendered this frame (they clone the templates + share the group).
	if (state.chunkMaterialMap) countGroups("chunkMaterials", state.chunkMaterialMap.values());
	out.uboDiag = {
		// Distinct UniformsGroup objects alive (≈ distinct binding points for the terrain).
		distinctLiveUniformsGroups: liveUboIds.size,
		groupsBySource: liveUboBySource,
		// The GL cap — WebGL2 spec floor is 24; ANGLE/SwiftShader reports 72.
		maxUniformBufferBindings: gl ? gl.getParameter(gl.MAX_UNIFORM_BUFFER_BINDINGS) : null,
		// The live three.js program count (the real driver of binding-point pressure).
		liveThreePrograms: renderer?.info?.programs?.length ?? null,
		// The manager's own per-set program cache size (distinct set-hashes ever created).
		managerProgramCount: manager?.programs?.size ?? null,
		// Warm keep-alive scratch count (how many programs we're holding resident across evictions).
		warmScratchCount: manager?.warmMaterials?.size ?? null,
		// Theoretical worst case if every distinct set-hash held its own UBO: managerProgramCount.
		worstCasePrograms_OneUboEach: manager?.programs?.size ?? null,
	};
	// ── Set7 params-by-source (2026-08-25): how many param READS in the live GLSL go through the
	//    MaterialParams UBO (u_p_<n>) vs a texture (u_variantParams / u_regionParams).  Excludes
	//    the `float u_p_<n>;` DECLARATION tokens (each param is declared once even if never read).
	if (manager) {
		const bySource = {uboReads: 0, variantTexReads: 0, regionTexReads: 0, rampVariantReads: 0};
		const perProgram: any[] = [];
		for (const p of [...(manager.programs?.values() ?? [])] as any[]) {
			const fs: string = p?.material?.fragmentShader ?? "";
			if (!fs) continue;
			// Distinct u_p_<n> READ references — skip tokens immediately preceded by a GLSL type
			// keyword (the declaration `float/vec4 u_p_<n>`), which are not reads.
			const uboReads = new Set<string>();
			const reUbo = /(?:^|[^A-Za-z0-9_])u_p_(\d+)\b/g; let m: RegExpExecArray|null;
			while ((m = reUbo.exec(fs)) != null) {
				const idx = m[1];
				const before = fs.slice(Math.max(0, m.index - 40), m.index);
				// A declaration line: the previous non-space token is `float`/`vec4` (and often
				// ends with `;`/`[6]` on the same statement).  A read is any other context.
				const declType = /\b(float|vec4)\s*$/.test(before);
				if (!declType) uboReads.add(`u_p_${idx}`);
			}
			// texelFetch(u_variantParams, ...) count (variant texture reads).
			const variantReads = (fs.match(/texelFetch\s*\(\s*u_variantParams/g) ?? []).length;
			const regionReads = (fs.match(/texelFetch\s*\(\s*u_regionParams/g) ?? []).length;
			// variantRamp(...) calls = 6-fetch ramp reads (already counted above, but flagged).
			const rampReads = (fs.match(/\bvariantRamp\s*\(/g) ?? []).length;
			bySource.uboReads += uboReads.size;
			bySource.variantTexReads += variantReads;
			bySource.regionTexReads += regionReads;
			bySource.rampVariantReads += rampReads;
			perProgram.push({setHash: p.setHash, uboReads: uboReads.size, variantTexReads: variantReads, regionTexReads: regionReads, rampVariantReads: rampReads});
		}
		out.uboDiag.paramsBySource = bySource;
		out.uboDiag.paramsPerProgram = perProgram;
	}
	return out;
}

/** Fresh deep-inspection snapshot of the splat/material-region state. */
export function terrainDebugRefresh(): any {
	const {regions, qt, manager, splatMap, chunkMaterialMap} = state;
	const out: any = {
		baseRecipeID: state.baseRecipeID,
		splatEnabled: state.splatEnabled,
		materialProgSetHash: state.materialProgSetHash,
		regions: regions.map(r=>({
			id: r.id,
			recipeID: r.pathFeature.recipeID,
			mode: r.pathFeature.mode,
			dominance: r.dominance,
			strokeWidth: r.pathFeature.strokeWidth ?? null,
			feather: r.pathFeature.feather ?? null,
			opaque: r.pathFeature.opaque ?? null,
			closed: r.closed,
			polylineCount: r.polylines.length,
			polylinePointCounts: r.polylines.map(p=>p.length),
			bbox: {x: r.bbox.x, y: r.bbox.y, width: r.bbox.width, height: r.bbox.height},
			firstPolyBBox: (()=>{
				const p = r.polylines[0]; if (!p) return null;
				let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
				for (const pt of p) { minX = Math.min(minX, pt.x); minY = Math.min(minY, pt.y); maxX = Math.max(maxX, pt.x); maxY = Math.max(maxY, pt.y); }
				return {minX, minY, maxX, maxY};
			})(),
		})),
	};
	if (qt) {
		const leaves = qt.collectLeaves();
		out.chunks = leaves.map(leaf=>{
			const splat = splatMap?.get(leaf);
			const mat = chunkMaterialMap?.get(leaf);
			// DataTexture .image.data: indices are RGBA8 (slot index ×4 per texel), distances RGBA32F.
			let indexData: Uint8Array|null = null;
			let distData: Float32Array|null = null;
			let regionData: Uint8Array|null = null;
			if (splat?.indexTex?.image?.data) indexData = new Uint8Array(splat.indexTex.image.data);
			if (splat?.weightTex?.image?.data) distData = new Float32Array(splat.weightTex.image.data);
			if (splat?.regionTex?.image?.data) regionData = new Uint8Array(splat.regionTex.image.data);
			// Set7 §9 — the SECOND distance texture (slots 4–7); the 8-lane grid below merges both.
			let distData2: Float32Array|null = null;
			if ((splat as any)?.weightTex2?.image?.data) distData2 = new Float32Array((splat as any).weightTex2.image.data);
			// Full 2D slot-index + region-payload grids (N×N; the slot index texel is 4 bytes —
			// the index is in .r; region payload in .r).
			const N = Math.round(Math.sqrt((indexData?.length ?? 0) / 4)) || 33;
			const slotGrid: number[][] = [];
			const regionGrid: number[][] = [];
			const distGrid: number[][] = [];
			if (indexData && N) {
				for (let r = 0; r < N; r++) {
					const row: number[] = [];
					for (let c = 0; c < N; c++) row.push(indexData[(r*N+c)*4]); // .r = slot index
					slotGrid.push(row);
				}
			}
			if (regionData && N) {
				for (let r = 0; r < N; r++) {
					const row: number[] = [];
					for (let c = 0; c < N; c++) row.push(regionData[(r*N+c)*4]); // .r = region payload
					regionGrid.push(row);
				}
			}
			if ((distData && N) || (distData2 && N)) {
				for (let r = 0; r < N; r++) {
					const row: number[] = [];
					for (let c = 0; c < N; c++) {
						const idx = (r*N+c)*4;
						// all 8 slots (Set7 §9 — the raise from 4): slots 0-3 from weightTex, 4-7 from weightTex2
						for (let s = 0; s < 4; s++) row.push(distData ? distData[idx+s] : 0);
						for (let s = 0; s < 4; s++) row.push(distData2 ? distData2[idx+s] : 0);
					}
					distGrid.push(row);
				}
			}
			return {
				bounds: {x: leaf.bounds.x, y: leaf.bounds.y, w: leaf.bounds.width, h: leaf.bounds.height},
				texVersion: leaf.chunk?.texVersion ?? 0,
				hasSplat: !!splat,
				splatTexVersion: splat?.texVersion ?? null,
				slotDominance: splat?.slotDominance ?? null,
				programSetHash: mat?.programSetHash ?? null,
				// 2026-08-26 — DEBUG SHIM probe: the chunk clone's u_debugShimColors palette (the
				// per-chunk slot→colour mapping setChunkSplatDist rewrites) + its recipe ids.
				// 2026-08-26 — black-chunk probe: WHICH variant-params texture instance is actually
				// bound on the chunk clone + its GL-side texture object id.  A stale binding (an
				// OLD instance from before the manager's growth swap) has a DIFFERENT id than the
				// manager's current one — and its GL allocation can be SMALLER than the current
				// buffer (the glTexSubImage2D "offset overflows" console warning).
				boundVariantTexId: (mat?.uniforms?.u_variantParams?.value as any)?.uuid ?? null,
				// 2026-08-26 — black-chunk probe: the bound splat texture instance ids too (the
				// index/distance textures are replaced wholesale on re-rasterise; a chunk whose
				// clone kept a disposed old instance would sample garbage).
				boundSplatIndexTexId: (mat?.uniforms?.u_splatIndex?.value as any)?.uuid ?? null,
				shimPalette: (mat?.uniforms?.u_debugShimColors?.value ?? null)?.map((v: any)=>[+v.x.toFixed(2), +v.y.toFixed(2), +v.z.toFixed(2), +v.w.toFixed(3)]) ?? null,
				chunkRecipeIDs: (splat as any)?.chunkRecipeIDs ?? null,
				// Set6 — the chunk's Layer-2 variant key (identity set).  Distinct identity sets
				// sharing a program hash show different keys here (the Grass3/Jungle fix).
				variantKey: (mat as any)?.variantKey ?? null,
				// Set7 — the live material's actual u_variantRow/u_variantParams (what the shader's
				// texelFetch reads).  Black while row>0 + bound ⇒ wrong row bytes (pack/layout bug).
				variantRow: mat?.uniforms?.u_variantRow?.value ?? null,
				variantParamsBound: !!mat?.uniforms?.u_variantParams?.value,
				// Whether this chunk's material even DECLARES the variant uniforms (a material built
				// without useVariantParams silently ignores setChunkVariantRow — no row is set).
				variantParamsDeclared: "u_variantRow" in (mat?.uniforms ?? {}),
				// Set6 debug — full slot-index / region-payload / distance grids (N×N) +
				// the material's bound UBO.  Lets us read the exact texel under the cursor.
				slotGrid,
				regionGrid,
				distGrid,
				boundParamScalars: (()=>{
					const ug = (mat as any)?.uniformsGroups;
					const group = Array.isArray(ug) ? ug[0] : null;
					if (!group || !group.uniforms) return null;
					const scalars: any = {};
					for (let i = 0; i < group.uniforms.length; i++) {
						const u = group.uniforms[i];
						const v = u?.value;
						if (typeof v == "number") scalars[`u_p_${i}`] = v;
					}
					return scalars;
				})(),
				boundParamRamps: (()=>{
					const ug = (mat as any)?.uniformsGroups;
					const group = Array.isArray(ug) ? ug[0] : null;
					if (!group || !group.uniforms) return null;
					const ramps: any = {};
					for (let i = 0; i < group.uniforms.length; i++) {
						const u = group.uniforms[i];
						const v = u?.value;
						// ramp = array of Uniform(vec4) OR a vec4; capture vec4s
						if (v && v.isVector4) {
							ramps[`u_p_${i}`] = v.toArray ? Array.from(v.toArray()) : null;
						}
					}
					return ramps;
				})(),
				// Set6 debug — the bound UniformsGroup id (to compare against the variant's group id).
				boundGroupId: (()=>{
					const ug = (mat as any)?.uniformsGroups;
					const group = Array.isArray(ug) ? ug[0] : null;
					return group ? group.id : null;
				})(),
			};
		});
		out.leafCount = leaves.length;
	}
	if (manager) {
		const m: any = manager;
		out.programs = m.programs ? [...m.programs.keys()] : [];
		out.resolvedSetKeys = m.resolvedSetHashByIds ? [...m.resolvedSetHashByIds.keys()] : [];
		// Set6 (2026-08-25) — the Layer-2 variant cache: identity-set → program+UBO.  Multiple
		// keys here per structural-hash program = distinct identities sharing a hash are being
		// distinguished (the Grass3/Jungle fix is engaged).
		out.variantKeys = m.variants ? [...m.variants.keys()] : [];
		out.variantCount = m.variants ? m.variants.size : 0;

		// Set7 black-terrain probe: if the manager's ROW STRIDE in variantData (floats between
		// rows) ≠ the DataTexture's texel-row stride (widthTexels*4), rows ≥1 read zeros → black.
		out.variantTextureDiag = (()=>{
			const tex = m.variantTexture;
			const data = m.variantData;
			if (!tex || !data) return {exists: false};
			const widthTexels = tex.image?.width ?? null;
			const floatWidth = m.variantRowFloatWidth ?? null;
			const height = tex.image?.height ?? null;
			const texelWidthBytes = 4; // RGBA32F
			const expectedRowFloatStride = widthTexels != null ? widthTexels * texelWidthBytes : null;
			const outV: any = {
				exists: true,
				widthTexels, floatWidth, height,
				nextVariantRow: m.nextVariantRow ?? null,
				freeList: (m.variantRowFreeList ?? []).slice(),
				refs: m.variantRowRefs ? Object.fromEntries(m.variantRowRefs) : {},
				expectedRowFloatStride, // what the texture view implies each row's float-stride is
				// The manager's WRITE stride when packing a row (row * variantRowFloatWidth * 4).
				// If this ≠ expectedRowFloatStride, rows land 4x-apart in the buffer → wrong texel rows.
				writeStride: floatWidth != null ? floatWidth * 4 : null,
				strideMismatch: expectedRowFloatStride != null && floatWidth != null
					? (floatWidth * 4) !== expectedRowFloatStride : null,
			};
			// Sample each variant's ACTUAL stored row bytes at BOTH possible strides, so we can see
			// whether the shader's texel row (expectedRowFloatStride) reads zeros vs the packer's
			// write offset (writeStride).  Compare row 1 (the first real variant) against row 0.
			const sampleRows = (row: number): any => {
				if (data.length < (row + 1) * (expectedRowFloatStride ?? 1)) return null;
				const atExpected = Array.from(data.subarray(row * (expectedRowFloatStride ?? 0), row * (expectedRowFloatStride ?? 0) + Math.min(floatWidth ?? 0, 8)));
				const atWrite = floatWidth != null ? Array.from(data.subarray(row * floatWidth * 4, row * floatWidth * 4 + Math.min(floatWidth, 8))) : null;
				const nonZeroAtExpected = (()=>{
					const s = expectedRowFloatStride ?? 0; let n = 0;
					for (let i = row * s; i < Math.min(data.length, row * s + (floatWidth ?? 0)); i++) if (data[i] !== 0) n++;
					return n;
				})();
				return {atExpected, atWrite, nonZeroAtExpected};
			};
			outV.row0 = sampleRows(0);
			outV.row1 = sampleRows(1);
			// For each live variant, where the packer THINKS its row lives vs where the shader reads it.
			outV.variants = m.variants ? [...m.variants.entries()].map(([key, v]: [string, any])=>({
				key,
				row: v?.row ?? null,
				recipeIDs: v?.recipeIDs ?? null,
				valuesLength: v?.values?.length ?? null,
			})) : [];
			return outV;
		})();
		// Set6 debug (Issue A) — introspect the chunk-set variant: its program's compile-time
		// recipe ids + param-layout keys + packed values, to see if overrides reach the slots.
		if (typeof m.__debugVariantInfo === "function") {
			out.chunkVariantDebug = m.__debugVariantInfo(["starfield", "user_TgLVoT9PSZ", "user_WyIaimZZRd", "water"], {useSplat: true, useRegionParams: true});
			// Also expose the manager so a session can query any identity set's variant.
			out.__manager = m;
		}
		// 2026-08-26 — black-chunk probe: expose the LIVE per-chunk clone materials so a session
		// can inspect a specific chunk's bound material (its three program, uniforms, UBO state).
		out.__chunkMats = chunkMaterialMap ?? null;
	}
	return out;
}

declare global {
	interface Window {
		/** Set12 debug hook — deep-inspection snapshot of the live splat/material-region state.
		 *  Call `window.lfTerrainDebug.refresh()` for a fresh read.  `programDiag()` reports the
		 *  program-compile count (getProgramInfoLog calls) + the three/manager program caches. */
		lfTerrainDebug?: {refresh(): any; programDiag(): any};
	}
}

// Install at module load; re-installed on hot-reload (the renderer re-pushes state each body).
if (typeof window !== "undefined") {
	window.lfTerrainDebug = {refresh: terrainDebugRefresh, programDiag: terrainProgramDiag};
}
